/**
 * Settings-to-runtime impact groups used by App and synth/keyboard wiring.
 * Determines which edits need geometry, tuning, input or output updates; keep this
 * aligned with persistence/settings-registry without turning cosmetic changes
 * into audio or canvas rebuilds.
 */

export const SETTINGS_IMPACT_FIELDS = {
  tuning: ["scale", "equivSteps", "reference_degree", "fundamental"],
  layout: ["rSteps", "drSteps", "center_degree", "hexSize", "rotation"],
  labels: [
    "note_names",
    "key_labels",
    "show_equaves",
    "heji_anchor_label",
    "heji_anchor_root_label",
    "heji_anchor_ratio",
    "heji_anchor_frequency",
    "heji_show_cents",
    "heji_tempered_only",
  ],
  colors: [
    "note_colors",
    "key_colors_mode",
    "spectrum_colors",
    "auto_colors",
    "fundamental_color",
    "prime_family_colors",
  ],
  inputBinding: [],
  inputRuntime: [
    "midiin_device",
    "midiin_steps_per_channel",
    "midiin_channel_group_size",
    "midiin_anchor_channel",
    "midiin_anchor_note",
    // Preset geometry anchors must survive live geometry/bypass transitions.
    "lumatone_anchor_note",
    "lumatone_anchor_channel",
    "exquis_anchor_note",
    "linnstrument_anchor_note",
    "linnstrument_anchor_channel",
    "haken_anchor_note",
    "midiin_channel_legacy",
    "midiin_controller_override",
    "midiin_mapping_target",
    "midiin_mpe_input",
    "midiin_mpe_lo_ch",
    "midiin_mpe_hi_ch",
    "midiin_bend_flip",
    "midiin_bend_range",
    "linnstrument_channel_allocation",
    "linnstrument_pitch_bend_mode",
    "linnstrument_pitch_bend_shape",
    "linnstrument_x_spike_reduction",
    "linnstrument_x_input_smoothing",
    "midiin_scale_bend_range",
    "hakenaudio_x_glide_shaping",
    "hakenaudio_x_glide_mode",
    "hakenaudio_glide_flip_cc",
    "hakenaudio_pressure_velocity",
    "hakenaudio_note_off_delay",
    "hakenaudio_raster_throttle_ms",
    "hakenaudio_raster_attack_suppression_ms",
    "hakenaudio_raster_stability",
    "hakenaudio_raster_filter_mode",
    "hakenaudio_raster_filter",
    "hakenaudio_raster_filter_snapshots",
    "hakenaudio_apply_raster_in_pitch_bending",
    "hakenaudio_shape_x_glide_to_raster",
    "hakenaudio_out_port",
    "midiin_pitchbend_mode",
    "midiin_pressure_mode",
    "midiin_modwheel_source",
    "midiin_modwheel_value",
    "midi_passthrough",
    "controller_anchor_note",
    "axis49_center_note",
    "tonalplexus_input_mode",
    "wheel_to_recent",
    "wheel_portamento",
    "wheel_portamento_time",
    "wheel_scale_aware",
    "midi_wheel_semitones",
    "midiin_mpe_manager_ch",
    "lumatone_led_sync",
    "lumatone_modwheel_timbre",
    "lumatone_foot_timbre",
    "lumatone_degree_filter_mode",
    "lumatone_degree_filter",
    "lumatone_degree_filter_snapshots",
    "exquis_led_sync",
    "exquis_orientation",
    "exquis_sustain_cc",
    "linnstrument_led_sync",
    "modulation_style",
  ],
  outputRuntime: [
    "instrument",
    "output_sample",
    "output_mts",
    "output_mpe",
    "output_mono",
    "mono_device",
    "mono_channel",
    "mono_bend_range",
    "mono_expression_y_cc",
    "mono_portamento",
    "mono_portamento_time",
    "output_mts_bulk",
    "output_osc",
    "midi_device",
    "midi_channel",
    "midi_mapping",
    "midi_velocity",
    "sysex_auto",
    "sysex_type",
    "device_id",
    "tuning_map_number",
    "mts_bulk_device",
    "mts_bulk_mode",
    "mts_bulk_channel",
    "mts_bulk_sysex_auto",
    "mts_bulk_device_id",
    "mts_bulk_tuning_map_number",
    "mts_bulk_tuning_map_name",
    "fluidsynth_device",
    "fluidsynth_channel",
    "output_fluidsynth",
    "fluidsynth_runtime_revision",
    "mpe_device",
    "mpe_lo_ch",
    "mpe_hi_ch",
    "mpe_pitchbend_range",
    "mpe_pitchbend_range_manager",
    "mpe_mode",
    "mpe_plus_output",
    "mpe_auto_generate_yz",
    "mpe_eagan_modwheel_brightness",
    "fluidsynth_out_port",
    "osc_bridge_url",
    "osc_local",
    "osc_server_off",
  ],
};

// Settings that are intentionally not part of Keys impact decisions. Keeping
// this list explicit makes new persistence keys fail registry coverage tests
// until their rendering/runtime impact is classified.
export const SETTINGS_IMPACT_IGNORED_FIELDS = {
  presetMetadata: ["name", "description", "short_description"],
  scaleEditorOnly: ["retuning_mode"],
  paletteUiOnly: [
    "heji_palette_visible",
    "heji_palette_structure",
    "heji_palette_deviation",
    "heji_palette_decimals",
  ],
  inputUiOnly: [],
  synthRuntimeOnly: [
    "osc_volume_pluck",
    "osc_volume_buzz",
    "osc_volume_formant",
    "osc_volume_saw",
    // Saved fader preference; live Brightness uses the imperative OSC path.
    "osc_brightness",
    "osc_quick_release",
    "osc_quick_release_time",
    "osc_quick_release_raster_only",
    "osc_retrigger_buzz_formant",
    "mpe_eagan_brightness",
    "mpe_eagan_tilt_eq",
    "mpe_eagan_pre_level",
    "mpe_eagan_post_level",
    "fluidsynth_preset",
  ],
  permissionState: ["webmidi_enabled", "webmidi_sysex_enabled", "webmidi_access"],
  ledDriverLifecycle: [
    "lumatone_out_port",
    "exquis_out_port",
    "linnstrument_out_port",
    "exquis_led_luminosity",
    "exquis_led_saturation",
  ],
};

export const SETTINGS_IMPACT_GROUPS = {
  structural: [
    ...SETTINGS_IMPACT_FIELDS.tuning,
    ...SETTINGS_IMPACT_FIELDS.layout,
    ...SETTINGS_IMPACT_FIELDS.labels,
  ],
  keysReconstruction: [
    ...SETTINGS_IMPACT_FIELDS.tuning,
    ...SETTINGS_IMPACT_FIELDS.layout,
    ...SETTINGS_IMPACT_FIELDS.inputBinding,
  ],
  musicalSurfaceReset: [...SETTINGS_IMPACT_FIELDS.tuning, ...SETTINGS_IMPACT_FIELDS.layout],
  colors: [
    ...SETTINGS_IMPACT_FIELDS.colors,
    ...SETTINGS_IMPACT_FIELDS.tuning,
    ...SETTINGS_IMPACT_FIELDS.labels,
  ],
  inputRuntime: SETTINGS_IMPACT_FIELDS.inputRuntime,
  outputRuntime: SETTINGS_IMPACT_FIELDS.outputRuntime,
};

export function settingsImpactSnapshot(settings, group, extra = {}) {
  const fields = SETTINGS_IMPACT_GROUPS[group] ?? SETTINGS_IMPACT_FIELDS[group];
  if (!fields) throw new Error(`Unknown settings impact group: ${group}`);
  const snapshot = {};
  for (const field of fields) snapshot[field] = settings?.[field];
  return { ...snapshot, ...extra };
}

export function settingsImpactKey(settings, group, extra = {}) {
  return JSON.stringify(settingsImpactSnapshot(settings, group, extra));
}
